import { Action } from "../Action/Action";
import { ActionHistoryI } from "./type";

class Node {
  private static _idCounter = 0;

  public id: number;
  public data: Action | null;
  public next: Node | null;
  public before: Node | null;

  constructor(data: Action | null = null, before: Node | null = null) {
    this.id = Node._idCounter++;
    this.data = data;
    this.next = null;
    this.before = before;
  }

  public static setIdCounter(value: number) {
    Node._idCounter = value;
  }
}

export default class ActionHistory implements Iterable<Action> {
  public head: Node = new Node();
  private nodes: Map<number, Node> = new Map();

  constructor() {
    this.nodes.set(this.head.id, this.head);
  }

  [Symbol.iterator](): Iterator<Action> {
    let head = this.head;
    return {
      next: (): IteratorResult<Action> => {
        const headCopy = head;
        head = head.before as Node;
        return { value: headCopy.data, done: true };
      },
    };
  }

  push(actionData: Action) {
    if (!this.head) {
      this.head = new Node(actionData);
      this.nodes.set(this.head.id, this.head);
    } else {
      if (this.head.next && this.head.next.data?.compare(actionData)) {
        this.head = this.head.next;
      } else {
        const temp = new Node(actionData, this.head);
        this.head.next = temp;
        this.head = temp;
        this.nodes.set(temp.id, temp);
      }
    }
  }

  public pop(): Action | null {
    if (this.hasUndo()) {
      const res = this.head.data;
      this.head = this.head.before as Node;
      return res;
    }
    return null;
  }

  public hasUndo(): boolean {
    return Boolean(this.head && this.head.before);
  }

  public hasRedo(): boolean {
    return Boolean(this.head && this.head.next);
  }

  public getRedo(): Action | null {
    if (this.hasRedo()) {
      return this.head.next?.data ?? null;
    } else {
      return null;
    }
  }

  clear(): void {
    this.head = new Node();
    this.nodes.clear();
    this.nodes.set(this.head.id, this.head);
  }

  serialize(): ActionHistoryI {
    const result = Array.from(this.nodes.values()).map((node) => ({
      id: node.id,
      data: node.data ? node.data.toJSON() : null,
      beforeId: node.before ? node.before.id : null,
      nextId: node.next ? node.next.id : null,
    }));

    return {
      headId: this.head.id,
      data: result,
    };
  }

  deserialize(serialized: ActionHistoryI): void {
    this.nodes.clear();
    const nodeMap = new Map<number, Node>();

    for (const { id, data } of serialized.data) {
      const node = new Node(data ? Action.fromJSON(data) : null);
      node.id = id;
      node.next = null;
      node.before = null;
      nodeMap.set(id, node);
    }

    for (const { id, beforeId, nextId } of serialized.data) {
      const node = nodeMap.get(id)!;
      if (beforeId !== null) node.before = nodeMap.get(beforeId) ?? null;
      if (nextId !== null) node.next = nodeMap.get(nextId) ?? null;
    }

    this.nodes = nodeMap;
    this.head = nodeMap.get(serialized.headId)!;

    const maxId = Math.max(...serialized.data.map((c) => c.id));
    Node.setIdCounter(maxId + 1);
  }
}
